import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createCn } from '../../helpers/utils'
import classes from './styles.module.scss'
import { useTranslation } from 'react-i18next'
import FileList from './components/list.component'
import { apiService } from '../../services/api.service'
import { Chevron, LayoutGridIcon, ListViewIcon } from '../../assets'
import { useMeetingsListAutoRefresh } from '../../hooks/use-meeting-auto-refresh.hook'
import { useMeetingsProgress } from '../../hooks/use-meeting-progress.hook'
import { DropResult } from '@hello-pangea/dnd'
import {
  applyManualOrder,
  loadMeetingsOrder,
  reorderMeetingIds,
  saveMeetingsOrder,
} from './utils/meetings-order'

const cn = createCn('files', classes)

export type Meeting = {
  id: string;
  name: string;
  date: string;
  duration: string;
  status: string;
  isFavorite: boolean;
  hasImg: boolean;
  /** Email владельца, если запись расшарена текущему пользователю (не его собственная). */
  sharedOwnerEmail?: string;
}

export type FilesViewMode = 'list' | 'grid';
export type FilesSortMode = 'date' | 'manual';
export type FilesAccessFilter = 'all' | 'mine' | 'shared';

export function pad(num: number): string {
  return num.toString().padStart(2, "0");
}

function formatApiDate(dateStr: string): string {
  if (!dateStr) return '';
  if (dateStr.includes('.')) return dateStr;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return dateStr;
  return `${pad(day)}.${pad(month)}.${year}`;
}

const generateMeetings = (count: number = 20): Meeting[] => {
  const result: Meeting[] = [];
  for (let i = 0; i < count; i++) {
    const hours = Math.floor(Math.random() * 3);
    const minutes = Math.floor(Math.random() * 60);
    const seconds = Math.floor(Math.random() * 60);

    const dateObj = new Date(
      Date.now() - Math.floor(Math.random() * 10000000000)
    );
    const day = pad(dateObj.getDate());
    const month = pad(dateObj.getMonth() + 1);
    const year = dateObj.getFullYear();
    result.push({
      id: `${i + 1}`,
      name: `Meeting ${i + 1}`,
      date: `${day}.${month}.${year}`,
      duration: `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`,
      status: 'completed',
      isFavorite: Math.random() > 0.5,
      hasImg: Math.random() > 0.5,
    });
  }
  return result;
}

const FilesPage = ({ type = 'files' }: { type?: 'files' | 'favorites' | 'trash' }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('')
  const [list, setList] = useState<Meeting[]>([])
  const [loading, setLoading] = useState(true)
  const [sortNewestFirst, setSortNewestFirst] = useState(true)
  const [sortMode, setSortMode] = useState<FilesSortMode>('date')
  const [manualOrder, setManualOrder] = useState<string[]>(() => loadMeetingsOrder(type))
  const [viewMode, setViewMode] = useState<FilesViewMode>('list')
  const [accessFilter, setAccessFilter] = useState<FilesAccessFilter>('all')

  useEffect(() => {
    const saved = loadMeetingsOrder(type);
    setManualOrder(saved);
    setSortMode(saved.length ? 'manual' : 'date');
  }, [type]);

  const loadMeetings = useCallback(async () => {
    if (type === 'trash') {
      setList([]);
      setLoading(false);
      return;
    }

    const [meetings, sharedWithMe] = await Promise.all([
      apiService.getMeetings(),
      // «Доступные мне» — вспомогательный список для бейджа/фильтра, не должен ронять загрузку основного списка.
      apiService.getSharedWithMe().catch(() => []),
    ]);
    const sharedOwnerByMeetingId = new Map(
      sharedWithMe.map(m => [m.id, m.owner_email || undefined] as const),
    );
    setList(meetings.map(m => ({
      id: m.id,
      name: m.name,
      date: formatApiDate(m.date),
      duration: m.duration || '00:00:00',
      status: m.status || 'processing',
      isFavorite: m.is_favorite ?? false,
      hasImg: m.has_img ?? false,
      sharedOwnerEmail: sharedOwnerByMeetingId.get(m.id),
    })));
  }, [type]);

  const hasProcessingMeetings = useMemo(
    () => list.some(item => item.status === 'processing'),
    [list],
  );

  useMeetingsListAutoRefresh({
    enabled: hasProcessingMeetings && !loading && type !== 'trash',
    onRefresh: loadMeetings,
  });

  // Задача L1: живой прогресс/ETA для карточек списка — один общий поллинг на все
  // обрабатываемые записи (см. useMeetingsProgress — сам ограничивает конкурентность).
  const processingIds = useMemo(
    () => list.filter(item => item.status === 'processing').map(item => item.id),
    [list],
  );
  const progressById = useMeetingsProgress(processingIds, !loading && type !== 'trash');

  const [page, setPage] = useState(1)
  const pageSize = 10;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    loadMeetings()
      .catch(() => {
        if (!cancelled) setList(generateMeetings(5));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [loadMeetings]);

  const parseMeetingDate = (value: string) => {
    const [day, month, year] = (value || '').split('.').map(Number);
    if (!day || !month || !year) return null;
    return new Date(year, month - 1, day);
  };

  const filteredList = useMemo(() => {
    const base = list;
    return base.filter(item => {
      const sourceText = `${item.name} ${item.duration} ${item.date}`.toLowerCase();
      const matchesSearch = sourceText.includes(search.toLowerCase());
      const matchesFavorite = type === 'favorites' ? item.isFavorite : true;
      const matchesAccess = type !== 'files' || accessFilter === 'all'
        ? true
        : accessFilter === 'shared'
          ? !!item.sharedOwnerEmail
          : !item.sharedOwnerEmail;
      return matchesSearch && matchesFavorite && matchesAccess;
    });
  }, [list, search, type, accessFilter]);

  const sortedList = useMemo(() => {
    let result: Meeting[];
    if (sortMode === 'manual' && manualOrder.length) {
      result = applyManualOrder(filteredList, manualOrder);
    } else {
      result = [...filteredList].sort((a, b) => {
        const dateA = parseMeetingDate(a.date)?.getTime() ?? 0;
        const dateB = parseMeetingDate(b.date)?.getTime() ?? 0;
        return sortNewestFirst ? dateB - dateA : dateA - dateB;
      });
    }
    return result;
  }, [filteredList, sortMode, manualOrder, sortNewestFirst]);

  const handleDragEnd = useCallback((result: DropResult) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    const offset = (page - 1) * pageSize;
    const fromIndex = offset + source.index;
    const toIndex = offset + destination.index;
    const baseIds = sortedList.map(item => item.id);
    const nextOrder = reorderMeetingIds(baseIds, fromIndex, toIndex);

    setManualOrder(nextOrder);
    saveMeetingsOrder(type, nextOrder);
    setSortMode('manual');
  }, [page, pageSize, sortedList, type]);

  const totalPages = Math.max(1, Math.ceil(sortedList.length / pageSize))
  const currentItems = sortedList.slice((page - 1) * pageSize, page * pageSize)

  useEffect(() => {
    setPage(1)
  }, [search, sortNewestFirst, accessFilter])

  const title = type === 'favorites'
    ? t('favorites.heading')
    : type === 'trash'
      ? t('trash.heading')
      : t('files.heading')

  const showCountBadge = type === 'files' || type === 'favorites';

  return (
    <div className={cn()}>
      <header className={cn('page-header')}>
        <div className={cn('header-top')}>
          <div className={cn('title-row')}>
            <h1 className={cn('title')}>{title}</h1>
            {showCountBadge && (
              <span className={cn('count-badge')}>
                {filteredList.length} {t('files.count-label')}
              </span>
            )}
          </div>
          {type === 'files' && (
            <p className={cn('subtitle')}>{t('files.subtitle')}</p>
          )}
          {hasProcessingMeetings && (
            <p className={cn('auto-sync-hint')}>{t('files.auto-sync-hint')}</p>
          )}
        </div>

        <div className={cn('toolbar')}>
          <div className={cn('search')}>
            <span className={cn('search-icon')} role="presentation">
              <svg viewBox="0 0 20 20" width="16" height="16">
                <circle cx="8" cy="8" r="5.2" stroke="currentColor" strokeWidth="1.5" fill="none" />
                <line x1="11.5" y1="11.5" x2="17" y2="17" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </span>
            <input
              type="text"
              placeholder={t('files.search-placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {type === 'files' && (
            <div className={cn('access-filter')} role="tablist" aria-label={t('files.access-filter-label')}>
              {(['all', 'mine', 'shared'] as const).map(key => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={accessFilter === key}
                  className={cn('access-filter-btn', { active: accessFilter === key })}
                  onClick={() => setAccessFilter(key)}
                >
                  {t(`files.access-filter-${key}`)}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className={cn('sort-button', { manual: sortMode === 'manual' })}
            onClick={() => {
              setSortNewestFirst((prev) => !prev);
              setSortMode('date');
            }}
          >
            <span>{sortMode === 'manual' ? t('files.sort-manual') : t('files.sort')}</span>
            <Chevron className={cn('sort-chevron', { asc: !sortNewestFirst })} />
          </button>
          <div className={cn('view-toggle')}>
            <button
              type="button"
              className={cn('view-btn', { active: viewMode === 'list' })}
              aria-label={t('files.view-list')}
              onClick={() => setViewMode('list')}
            >
              <ListViewIcon />
            </button>
            <button
              type="button"
              className={cn('view-btn', { active: viewMode === 'grid' })}
              aria-label={t('files.view-grid')}
              onClick={() => setViewMode('grid')}
            >
              <LayoutGridIcon />
            </button>
          </div>
        </div>
      </header>

      <div className={cn('content', { animated: type === 'files' })}>
        {loading && <p className={cn('loading')}>{t('files.loading')}</p>}
        <FileList
          setList={setList}
          currentItems={currentItems}
          filteredList={sortedList}
          pageSize={pageSize}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          type={type}
          viewMode={viewMode}
          onDragEnd={handleDragEnd}
          progressById={progressById}
        />
      </div>
    </div>
  )
}

export default FilesPage
